import { useEffect, useState } from "react";
import Button from "../../ui/buttons/Button";
import DropdownSelectPill from "../../ui/inputs/DropdownSelectPill";
import rocket from "../../assets/icons/rocket.svg";
import rocketHover from "../../assets/icons/rocket-hover.svg";
import { mailsService } from "../../services/mails.service";
import { templatesService } from "../../services/templates.service";
import FloatingModal from "../../ui/Modals/FloatingModal";
import { useLeadContext } from "../../contexts/LeadContext";

function extractBackendError(errorData) {
  if (!errorData) return "Error al crear el mail";
  if (typeof errorData === "string") return errorData;
  const firstValue = Object.values(errorData)[0];
  if (Array.isArray(firstValue)) return firstValue[0] || "Error al crear el mail";
  if (firstValue) return String(firstValue);
  return "Error al crear el mail";
}

function NewMailModal({
  leads,
  onClose,
  onMailCreated,
  onNewMailError,
}) {
  const { responsible } = useLeadContext();
  const [cuenta, setCuenta] = useState({});
  const [sendHovered, setSendHovered] = useState(false);
  const [receptor, setReceptor] = useState();
  const [templateGroup, setTemplateGroup] = useState();
  const [templateGroups, setTemplateGroups] = useState([]);
  const [diasReminder, setDiasReminder] = useState(0);
  const [mailCreated, setMailCreated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    templatesService
      .fetchTemplateGroups()
      .then((response) => {
        const groups = Array.isArray(response)
          ? response
          : (response?.results ?? []);
        setTemplateGroups(groups);
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage("No se pudieron cargar los template groups.");
      });
  }, []);

  const isEmptyObject = (target) => {
    return (
      target &&
      typeof target === "object" &&
      !Array.isArray(target) &&
      Object.keys(target).length === 0
    );
  };

  const disableSend =
    isEmptyObject(cuenta) || !receptor || !templateGroup || !diasReminder;

  const accountsOptions = () =>
    responsible.map((account) => ({ value: account.id, label: account.name }));

  const getAccountId = (mailCorp) => {
    if (!mailCorp || isEmptyObject(mailCorp)) return null;
    if (mailCorp.account?.id) return mailCorp.account.id;
    if (typeof mailCorp.account === "number") return mailCorp.account;
    if (mailCorp.account_id) return mailCorp.account_id;
    return null;
  };

  const receptorOptions = () => {
    const accountId = getAccountId(cuenta);
    if (!accountId) return [];
    return leads
      .filter((lead) => lead.account?.id === accountId)
      .map((lead) => ({ value: lead.id, label: lead.lead_name }));
  };

  const templateOptions = () => {
    const accountId = getAccountId(cuenta);
    if (!accountId) return [];
    return templateGroups
      .filter((group) => group.account?.id === accountId)
      .map((group) => ({
        value: group.id,
        label: group.group?.name ?? group.name,
      }));
  };

  const handleCuenta = (idCuenta) => {
    const found = responsible.find((account) => Number(account.id) === Number(idCuenta));
    if (found) setCuenta(found);
  };

  const handleReceptor = (idReceptor) => {
    const foundLead = leads.find((lead) => Number(lead.id) === Number(idReceptor));
    if (foundLead) setReceptor(foundLead);
  };

  const handleTemplate = (idTemplate) => {
    const foundTemplate = templateGroups.find(
      (group) => Number(group.id) === Number(idTemplate),
    );
    if (foundTemplate) setTemplateGroup(foundTemplate);
  };

  const sendMail = async () => {
    setErrorMessage("");
    const mailData = {
      mail_corp: cuenta?.id,
      lead: receptor?.id,
      template_group: templateGroup?.id,
      reminder_days: diasReminder,
    };

    try {
      await mailsService.createMailApi(mailData);
      await onMailCreated?.();
      setMailCreated(true);
    } catch (error) {
      const message = extractBackendError(error?.response?.data);
      if (onNewMailError) {
        onNewMailError(message);
      } else {
        setErrorMessage(message);
      }
    }
  };

  if (mailCreated) {
    return (
      <FloatingModal width="fit-content" bottom={56} gap={20}>
        <span style={{ fontSize: 16, fontWeight: 500, color: "var(--text-primary)" }}>
          ¡Mail creado exitosamente!
        </span>
        <Button tone="green" height={36} style={{ minWidth: 80 }} onClick={onClose}>
          Ok
        </Button>
      </FloatingModal>
    );
  }

  if (errorMessage) {
    return (
      <FloatingModal width={480} bottom={56} direction="column" gap={16} onClickOutside={onClose}>
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-error, #E53E3E)" }}>
          {errorMessage}
        </span>
        <Button variant="secondary" tone="blue" height={44} onClick={() => setErrorMessage("")}>
          Volver
        </Button>
      </FloatingModal>
    );
  }

  return (
    <FloatingModal width={780} bottom={56} onClickOutside={onClose}>
      <DropdownSelectPill
        options={accountsOptions()}
        placeholder="Cuenta"
        value={cuenta.id}
        onChange={(selectedValue) => handleCuenta(selectedValue)}
        chevronSize={16}
        dropUp
        menuMinWidth={180}
        style={{
          backgroundColor: `var(--surface-input-hover)`,
          height: 50,
          color: `var(--text-primary)`,
        }}
      />

      <DropdownSelectPill
        options={receptorOptions()}
        placeholder="Receptor"
        value={receptor?.id}
        onChange={(selectedValue) => handleReceptor(selectedValue)}
        chevronSize={16}
        dropUp
        menuMaxHeight={320}
        menuMinWidth={240}
        style={{
          backgroundColor: `var(--surface-input-hover)`,
          height: 50,
          color: `var(--text-primary)`,
        }}
        disabled={isEmptyObject(cuenta)}
      />

      <DropdownSelectPill
        options={templateOptions()}
        placeholder="Template Group"
        value={templateGroup?.id}
        onChange={(selectedValue) => handleTemplate(selectedValue)}
        chevronSize={16}
        dropUp
        style={{
          backgroundColor: `var(--surface-input-hover)`,
          width: 160,
          height: 50,
          color: `var(--text-primary)`,
        }}
        disabled={isEmptyObject(cuenta)}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          backgroundColor: "var(--surface-input-hover)",
          borderRadius: 14,
          padding: "0 8px",
          minWidth: 130,
          height: 50,
          color: "var(--text-primary)",
          opacity: isEmptyObject(cuenta) ? 0.5 : 1,
          pointerEvents: isEmptyObject(cuenta) ? "none" : "auto",
        }}
      >
        <button
          type="button"
          disabled={diasReminder <= 1}
          onClick={() => setDiasReminder((prev) => Math.max(1, prev - 1))}
          style={{
            width: 24,
            height: 24,
            border: "none",
            borderRadius: "50%",
            backgroundColor: "transparent",
            color: "var(--text-primary)",
            cursor: diasReminder <= 1 ? "not-allowed" : "pointer",
            fontSize: 16,
            fontWeight: 700,
            opacity: diasReminder <= 1 ? 0.4 : 1,
          }}
        >
          -
        </button>

        <span
          style={{
            minWidth: 74,
            textAlign: "center",
            fontSize: 12,
            whiteSpace: "nowrap",
          }}
        >
          {diasReminder} Días de Reminder
        </span>

        <button
          type="button"
          onClick={() => setDiasReminder((prev) => prev + 1)}
          style={{
            width: 24,
            height: 24,
            border: "none",
            borderRadius: "50%",
            backgroundColor: "transparent",
            color: "var(--text-primary)",
            cursor: "pointer",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          +
        </button>
      </div>

      <Button
        tone="green"
        iconRight={
          <img
            src={sendHovered ? rocketHover : rocket}
            alt=""
            style={{ width: 35, height: 35, display: "block" }}
          />
        }
        style={{ minWidth: 90, height: 50 }}
        disabled={disableSend}
        onClick={sendMail}
        onMouseEnter={() => setSendHovered(true)}
        onMouseLeave={() => setSendHovered(false)}
      >
        Send Mails
      </Button>
    </FloatingModal>
  );
}

export default NewMailModal;
